import { useEffect } from "react"
import { action_getCheckoutProducts, action_getOrderDetails } from "src/actions/checkout"
import { WCStoreCreateOrderResponseData } from "src/data/woocommerce/wcStoreTypes"
import { useIsClient } from "src/hooks/useIsClient"
import { refreshCartId } from "src/tracking/state"
import { StorefrontOrder } from "src/types/storefront"
import { createPersistentStore } from "src/utilities/localStorage/create"
import { trackPurchase } from "../../tracking/events"
import { mapOrder } from "../helpers/order"

export type CheckoutPaymentData = {
  storeOrder: WCStoreCreateOrderResponseData | null
  elventOrder: StorefrontOrder | null
  loading: boolean
}

const defaultState: CheckoutPaymentData = {
  storeOrder: null,
  elventOrder: null,
  loading: false,
}

const checkoutOrderStore = createPersistentStore(() => defaultState, {
  name: "checkout-order",
  version: 2,
  partialize: (state) => {
    return {
      storeOrder: state.storeOrder,
    }
  },
})

let promise: Promise<void> | null = null

async function refetchOrderState() {
  const order = checkoutOrderStore.getState().storeOrder
  const id = order?.order_id
  const key = order?.order_key
  const billingEmail = order?.billing_address.email

  if (!id || !key || !billingEmail) {
    return
  }

  if (!promise) {
    checkoutOrderStore.setState({ loading: true })

    promise = Promise.all([
      // wrap-line
      action_getOrderDetails(id),
      action_getCheckoutProducts(),
    ])
      .then(([adminOrder, products]) => {
        const elventOrder = mapOrder(adminOrder, products)

        checkoutOrderStore.setState({
          elventOrder,
          storeOrder: {
            // The completed order has a different format so we can't just
            // override the state. Currently, we only care about updating the
            // status.
            ...order,
            status: adminOrder.status,
          },
        })
      })
      .catch(console.error)

    await promise

    trackPurchase()
    refreshCartId()
    checkoutOrderStore.setState({ loading: false })
  }

  return promise
}

export function getStoreOrder() {
  checkoutOrderStore.getState().storeOrder
}

export function setStoreOrder(storeOrder: WCStoreCreateOrderResponseData) {
  checkoutOrderStore.setState({ storeOrder })
}

export function useStoreOrder() {
  return checkoutOrderStore((state) => state.storeOrder)
}

export function getOrder() {
  return checkoutOrderStore.getState().elventOrder
}

export function useOrder() {
  useEffect(() => {
    void refetchOrderState()
  }, [])

  return checkoutOrderStore((state) => state.elventOrder)
}

export function useOrderLoading() {
  const isClient = useIsClient()
  const loading = checkoutOrderStore((state) => state.loading)

  return isClient && loading
}

export function clearOrder() {
  checkoutOrderStore.setState(defaultState)
}
